<div class="date-range-picker" [attr.data-testid]="testId()">
	<div
		cdkOverlayOrigin
		#trigger="cdkOverlayOrigin"
		class="date-picker-trigger"
		[class.date-picker-trigger--visually-hidden]="connectedOverlayOrigin()"
		[class.date-picker-trigger--disabled]="disabled()"
		[class.date-picker-trigger--display-only]="displayOnly()"
		[attr.data-testid]="testId() + '-trigger'"
		[attr.aria-disabled]="disabled() || displayOnly()"
		(click)="open()"
	>
		<mat-form-field
			appearance="fill"
			subscriptSizing="dynamic"
			class="date-picker-field"
			[class.date-picker-field--has-value]="displayValue()"
		>
			<mat-label>{{ fieldLabel() }}</mat-label>
			<input
				matInput
				readonly
				[value]="displayValue()"
				[attr.aria-label]="'Date range'"
				[attr.aria-haspopup]="displayOnly() ? null : 'dialog'"
				[attr.aria-expanded]="displayOnly() ? null : isOpen()"
			/>
			<mat-icon
				matSuffix
				class="material-symbols-outlined"
				aria-hidden="true"
				>date_range</mat-icon
			>
		</mat-form-field>
	</div>

	@if (!displayOnly()) {
	<ng-template
		cdkConnectedOverlay
		[cdkConnectedOverlayOrigin]="connectedOverlayOrigin() ?? trigger"
		[cdkConnectedOverlayOpen]="isOpen()"
		[cdkConnectedOverlayPositions]="overlayPositions"
		[cdkConnectedOverlayPush]="true"
		[cdkConnectedOverlayViewportMargin]="16"
		(overlayOutsideClick)="onOverlayOutsideClick($event)"
	>
		<div
			class="picker-panel custom-ckd-container"
			[attr.data-picker-scope]="panelScopeId"
			jeptoDateRangePickerPanel
			#pickerPanel="jeptoDateRangePickerPanel"
			[firstDayOfWeek]="calendarFirstDayIndex()"
			role="dialog"
			aria-label="Date range picker"
			[attr.data-testid]="testId() + '-panel'"
		>
			<div class="picker-panel__body">
				<mat-action-list
					class="picker-panel__presets"
					[attr.data-testid]="testId() + '-presets'"
				>
					@for (preset of presetOptions(); track preset.id) {
						<button
							mat-list-item
							type="button"
							[attr.data-testid]="
								testId() + '-preset-' + preset.id
							"
							[activated]="activePresetId() === preset.id"
							[class.picker-panel__preset--active]="
								activePresetId() === preset.id
							"
							[class.picker-panel__preset--custom]="
								preset.id === 'custom'
							"
							(click)="selectPreset(preset)"
						>
							{{ preset.label }}
						</button>
					}
					@if (enableDynamicRanges()) {
						<button
							mat-list-item
							type="button"
							[attr.data-testid]="testId() + '-preset-dynamic'"
							[activated]="
								activeView() === 'dynamic' ||
								isDynamicRangeSelectionActive()
							"
							[class.picker-panel__preset--active]="
								activeView() === 'dynamic' ||
								isDynamicRangeSelectionActive()
							"
							(click)="showDynamicForm()"
						>
							Dynamic range
						</button>
					}
				</mat-action-list>

				<div
					class="picker-panel__content"
					[attr.data-testid]="testId() + '-content'"
				>
					@switch (activeView()) {
						@case ('calendar') {
							<div
								class="picker-panel__dual-calendar"
								[attr.data-testid]="testId() + '-calendar'"
							>
								<div
									class="picker-panel__calendar-month picker-panel__calendar-month--start"
								>
									@for (
										slot of leftCalendarSlots();
										track slot.trackId
									) {
										<mat-calendar
											[selected]="calendarDisplayRange()"
											[minDate]="minDate()"
											[maxDate]="maxDate()"
											[startAt]="slot.startAt"
											(selectedChange)="
												onCalendarDateSelected($event)
											"
											aria-label="Calendar start month"
										></mat-calendar>
									}
								</div>
								<div
									class="picker-panel__calendar-month picker-panel__calendar-month--end"
								>
									@for (
										slot of rightCalendarSlots();
										track slot.trackId
									) {
										<mat-calendar
											[selected]="calendarDisplayRange()"
											[minDate]="minDate()"
											[maxDate]="maxDate()"
											[startAt]="slot.startAt"
											(selectedChange)="
												onCalendarDateSelected($event)
											"
											aria-label="Calendar end month"
										></mat-calendar>
									}
								</div>
							</div>
						}
						@case ('dynamic') {
							<div
								class="picker-panel__dynamic-form"
								[attr.data-testid]="testId() + '-dynamic-form'"
							>
								<h4 class="picker-panel__form-title">
									Dynamic Relative Range
								</h4>
								<div class="picker-panel__start-end-row">
									<div class="picker-panel__form-section">
										<label
											class="picker-panel__section-label picker-panel__section-label--emphasis"
											>Start</label
										>
										<div class="picker-panel__start-toggle">
											<mat-radio-group
												[ngModel]="startType()"
												(ngModelChange)="
													startType.set($event)
												"
											>
												<mat-radio-button
													value="relative"
													>Relative</mat-radio-button
												>
												<mat-radio-button value="fixed"
													>Fixed
													date</mat-radio-button
												>
											</mat-radio-group>
										</div>
										@if (startType() === 'relative') {
											<div
												class="picker-panel__input-row"
											>
												<mat-form-field
													appearance="outline"
													subscriptSizing="dynamic"
													class="picker-panel__offset-input picker-panel__dynamic-field"
												>
													<input
														matInput
														type="number"
														min="1"
														[ngModel]="
															startOffset()
														"
														(ngModelChange)="
															startOffset.set(
																$event
															)
														"
													/>
												</mat-form-field>
												<mat-form-field
													appearance="outline"
													subscriptSizing="dynamic"
													class="picker-panel__unit-select picker-panel__dynamic-field"
												>
													<mat-select
														[ngModel]="startUnit()"
														(ngModelChange)="
															startUnit.set(
																$event
															)
														"
													>
														@for (
															unit of unitOptions;
															track unit.value
														) {
															<mat-option
																[value]="
																	unit.value
																"
															>
																{{
																	startOffset() ===
																	1
																		? unit.label
																		: unit.pluralLabel
																}}
																ago
															</mat-option>
														}
													</mat-select>
												</mat-form-field>
											</div>
										}
										@if (startType() === 'fixed') {
											<div
												class="picker-panel__input-row picker-panel__fixed-date-row"
											>
												<mat-form-field
													appearance="outline"
													subscriptSizing="dynamic"
													class="picker-panel__date-input picker-panel__dynamic-field"
												>
													<input
														matInput
														[matDatepicker]="
															fixedStartPicker
														"
														[ngModel]="
															fixedStartDateForPicker()
														"
														(ngModelChange)="
															fixedStartDate.set(
																$event
															)
														"
														aria-label="Fixed start date"
													/>
													<mat-datepicker-toggle
														matIconSuffix
														[for]="fixedStartPicker"
													></mat-datepicker-toggle>
													<mat-datepicker
														#fixedStartPicker
														[min]="minDate()"
														[max]="maxDate()"
													></mat-datepicker>
												</mat-form-field>
											</div>
										}
									</div>
									<div class="picker-panel__form-section">
										<label
											class="picker-panel__section-label picker-panel__section-label--emphasis"
											>End</label
										>
										<div class="picker-panel__end-toggle">
											<mat-radio-group
												[ngModel]="
													endIsToday()
														? 'today'
														: 'relative'
												"
												(ngModelChange)="
													endIsToday.set(
														$event === 'today'
													)
												"
											>
												<mat-radio-button
													value="relative"
													>Relative</mat-radio-button
												>
												<mat-radio-button value="today"
													>Today</mat-radio-button
												>
											</mat-radio-group>
										</div>
										@if (!endIsToday()) {
											<div
												class="picker-panel__input-row"
											>
												<mat-form-field
													appearance="outline"
													subscriptSizing="dynamic"
													class="picker-panel__offset-input picker-panel__dynamic-field"
												>
													<input
														matInput
														type="number"
														min="0"
														[ngModel]="endOffset()"
														(ngModelChange)="
															endOffset.set(
																$event
															)
														"
													/>
												</mat-form-field>
												<mat-form-field
													appearance="outline"
													subscriptSizing="dynamic"
													class="picker-panel__unit-select picker-panel__dynamic-field"
												>
													<mat-select
														[ngModel]="endUnit()"
														(ngModelChange)="
															endUnit.set($event)
														"
													>
														@for (
															unit of unitOptions;
															track unit.value
														) {
															<mat-option
																[value]="
																	unit.value
																"
															>
																{{
																	endOffset() ===
																	1
																		? unit.label
																		: unit.pluralLabel
																}}
																ago
															</mat-option>
														}
													</mat-select>
												</mat-form-field>
											</div>
										}
									</div>
								</div>
								<div
									class="picker-panel__preview-section"
									[class.picker-panel__preview-section--error]="
										!!dynamicRangeValidationError()
									"
									role="region"
									aria-label="Date range preview"
									[attr.data-testid]="testId() + '-preview'"
								>
									<p
										class="picker-panel__preview-section__title"
									>
										Preview
									</p>
									<p
										class="picker-panel__preview-section__body"
									>
										{{ dynamicPreviewLabel() }}
									</p>
									<p
										class="picker-panel__preview-section__body"
									>
										{{ dynamicPreviewDates().start }} →
										{{ dynamicPreviewDates().end }}
									</p>
									@if (
										dynamicRangeValidationError();
										as error
									) {
										<p
											class="picker-panel__validation-error"
											role="alert"
										>
											{{ error }}
										</p>
									}
								</div>
							</div>
						}
						@default {
							<div
								class="picker-panel__dual-calendar"
								[attr.data-testid]="testId() + '-calendar'"
							>
								<div
									class="picker-panel__calendar-month picker-panel__calendar-month--start"
								>
									@for (
										slot of leftCalendarSlots();
										track slot.trackId
									) {
										<mat-calendar
											[selected]="calendarDisplayRange()"
											[minDate]="minDate()"
											[maxDate]="maxDate()"
											[startAt]="slot.startAt"
											(selectedChange)="
												onCalendarDateSelected($event)
											"
											aria-label="Calendar start month"
										></mat-calendar>
									}
								</div>
								<div
									class="picker-panel__calendar-month picker-panel__calendar-month--end"
								>
									@for (
										slot of rightCalendarSlots();
										track slot.trackId
									) {
										<mat-calendar
											[selected]="calendarDisplayRange()"
											[minDate]="minDate()"
											[maxDate]="maxDate()"
											[startAt]="slot.startAt"
											(selectedChange)="
												onCalendarDateSelected($event)
											"
											aria-label="Calendar end month"
										></mat-calendar>
									}
								</div>
							</div>
						}
					}
				</div>
			</div>

			<div
				class="picker-panel__actions"
				[attr.data-testid]="testId() + '-actions'"
			>
				<div class="picker-panel__actions-leading">
					@if (
						showWeekStartSelector() && isWeekStartSelectorEnabled()
					) {
						<jepto-ui-select
							class="picker-panel__week-start-field"
							label="Start day of the Week"
							appearance="outline"
							[options]="panelWeekStartSelectOptions"
							[useInputValue]="true"
							[inputValue]="tempWeekStart()"
							(valueChange)="onPanelWeekStartChange($event.value)"
							(selectionChange)="
								onPanelWeekStartChange($event.value)
							"
							[attr.data-testid]="testId() + '-week-start'"
							[testId]="testId() + '-week-start-trigger'"
							[fieldBoundaryTestId]="
								testId() + '-week-start-field'
							"
						/>
					}
				</div>
				<div class="picker-panel__action-buttons">
					<button
						mat-button
						type="button"
						class="picker-panel__cancel"
						[attr.data-testid]="testId() + '-cancel'"
						(click)="close()"
					>
						Cancel
					</button>
					<button
						mat-flat-button
						color="primary"
						type="button"
						class="picker-panel__done"
						[attr.data-testid]="testId() + '-done'"
						(click)="apply()"
						[disabled]="
							activeView() === 'dynamic' &&
							dynamicRangeValidationError()
						"
					>
						Done
					</button>
				</div>
			</div>
		</div>
	</ng-template>
	}
</div>
